/**
 * spreadsheetSpec — Foglio di Calcolo (Excel / CSV / Google Sheets).
 *
 * V1 source-of-truth: ``ando-frontend/app/pages/EnterpriseHubPage.tsx``
 * (the "Foglio di Calcolo" tile, line 334-345) +
 * ``ando-frontend/app/components/enterprise/ConnectorWizard.tsx`` (the
 * inline ``spreadsheetSource`` / ``uploadedFile`` / ``spreadsheetSelectedSheets``
 * / ``selectedCloudFile`` / ``selectedDriveConn`` state machine).
 *
 * Three source-modes (vendors):
 *
 *   1. ``upload``        — local file (``.xlsx`` / ``.csv``)
 *   2. ``google_drive``  — pick from an already-connected Drive account
 *   3. ``onedrive``      — pick from an already-connected OneDrive account
 *
 * The wizard's standard ``ConnectAccountDialog`` (OAuth + credentials_form
 * only) cannot host this flow — the route mounts a dedicated
 * ``SpreadsheetConnectDialog`` instead when ``spec.capability === "spreadsheet"``.
 * The spec stays declarative so the catalog row, the empty-state, the
 * benefits grid and AddAccountMenu all keep working through the shared
 * primitives.
 *
 * Steps are described declaratively so a future re-port onto a generic
 * ``<ConnectorWizard>`` shell can pick them up without changing the spec.
 *
 * ``api`` — the connector is created BE-side via
 * ``POST /enterprise/connectors/import-spreadsheet`` (NOT the unified
 * ``/connectors/{cap}/start`` surface — V1 surface is preserved verbatim).
 * The api adapter therefore returns an empty status / connection list and
 * a no-op ``connect()`` — the real action runs through the
 * ``useImportSpreadsheet`` mutation owned by the dialog.
 */

import type {
  BaseConnection,
  CapabilityWizardSpec,
  ConnectResult,
  ConnectorApi,
  ConnectorStatus,
} from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Cloud, FileSpreadsheet, Layers, RefreshCcw, Sparkles, Upload } from "lucide-react";

import { GoogleDriveIcon, OneDriveIcon } from "../components/vendorIcons";

// Vendor ids accepted by ``SpreadsheetConnectDialog``. Re-exported from
// the spec so $capability.tsx + tests can narrow the route-level
// ``selectedVendor: string | null`` to a SpreadsheetSourceMode without
// importing from the dialog component (avoids a circular dep + keeps
// the spec the single source of truth for the legal vendor ids).
export type SpreadsheetSourceMode = "upload" | "google_drive" | "onedrive";

/** Type-guard mirroring V1's spreadsheet source-mode runtime check. */
export function isSpreadsheetSourceMode(
  value: string | null | undefined,
): value is SpreadsheetSourceMode {
  return value === "upload" || value === "google_drive" || value === "onedrive";
}

/** Accept list for the upload-mode file input (V1 parity, includes legacy .xls). */
export const SPREADSHEET_FILE_ACCEPT =
  ".xlsx,.xls,.csv," +
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "application/vnd.ms-excel," +
  "text/csv";

/**
 * Hard upload limit. Bumped to 500 MB (giugno 12, 2026) per coprire i
 * fogli "real-world" con tante righe / molte sheet / immagini embedded
 * — il vecchio limite di 50 MB rifiutava workbook industriali legittimi.
 * Il BE applica lo stesso cap (``_SPREADSHEET_MAX_SIZE`` in
 * ``enterprise_connectors.py``): tieni i due valori sincronizzati,
 * altrimenti il FE accetta e il BE rifiuta con 413.
 */
export const SPREADSHEET_MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

/** ``true`` when the file name extension is one of the supported spreadsheet formats. */
export function isSpreadsheetFile(name: string): boolean {
  return /\.(xlsx|xls|csv)$/i.test(name);
}

export interface SpreadsheetConnection extends BaseConnection {
  /** Source-mode the connector was created from. */
  readonly source_provider?: "upload" | "google_drive" | "onedrive";
  /** Human label set at import-time (matches BE ``connector_name``). */
  readonly connector_name?: string;
  /** Number of sheets imported (Excel multi-sheet). */
  readonly sheet_count?: number;
}

const iconCls = "h-4 w-4";

// ── Inert API adapter ────────────────────────────────────────────────────
// Spreadsheet uses the legacy V1 endpoint (``/enterprise/connectors/import-
// spreadsheet``), NOT the unified ``/connectors/{cap}/start`` surface. The
// declarative spec still needs an api binding so AddAccountMenu /
// ConnectorWizard share the same shape — we wire a no-op adapter that
// returns empty status + empty list. The real upload happens via
// ``useImportSpreadsheet`` mounted inside the dialog.
const inertApi: ConnectorApi<SpreadsheetConnection> = {
  async getStatus(): Promise<ConnectorStatus> {
    return { enabled: true };
  },
  async listConnections(): Promise<readonly SpreadsheetConnection[]> {
    return [];
  },
  async connect(): Promise<ConnectResult> {
    // The dialog short-circuits BEFORE calling connect() — this is a
    // structural fallback so the ConnectorApi contract stays satisfied
    // for callers that introspect ``spec.api``.
    return {};
  },
  async disconnect(): Promise<void> {
    // Disconnection routes through the standard
    // ``DELETE /connectors/spreadsheet/{id}`` surface in
    // ``useDisconnectConnection`` — no per-spec override needed.
  },
  async sync(): Promise<{ readonly in_flight: boolean }> {
    return { in_flight: false };
  },
};

export const spreadsheetSpec: CapabilityWizardSpec<SpreadsheetConnection> = {
  capability: "spreadsheet",
  title: msg`Foglio di Calcolo`,
  subtitle: "Excel (.xlsx), CSV, Google Sheets",
  icon: <FileSpreadsheet className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  // June 3 2026 — "Aggiungi account" was misleading for a file/workbook
  // attach (no OAuth credentials, no "account" mental model). Switch the
  // noun via the existing DRY system (``resolveConnectNoun`` reads these
  // three fields). Filesystem already does this with ``"cartella"``.
  // ``file`` is invariant in Italian plural, so the plural is identical.
  connectNoun: "file",
  connectNounPlural: "file",
  connectNounGender: "m",

  vendors: [
    {
      id: "upload",
      label: msg`Carica file`,
      icon: <Upload className={iconCls} aria-hidden />,
      description: "Excel (.xlsx) o CSV",
    },
    {
      id: "google_drive",
      label: msg`Da Google Drive`,
      icon: <GoogleDriveIcon className={iconCls} />,
      description: msg`Richiede Google Drive collegato`,
    },
    {
      id: "onedrive",
      label: msg`Da OneDrive`,
      icon: <OneDriveIcon className={iconCls} />,
      description: msg`Richiede OneDrive collegato`,
    },
  ],

  // Two declarative steps — the custom dialog interprets them via the
  // ``vendor`` selection (``state.vendor``):
  //   - ``source`` (kind=select) → vendor-driven; pickers are dialog-local
  //     because they branch on file-input vs cloud-picker
  //   - ``sheets`` (kind=select) → multi-checkbox sheet picker, only
  //     visible after the BE preview parses the workbook (Excel) or auto-
  //     skipped for CSV
  steps: [
    {
      id: "source",
      kind: "select",
      title: msg`Sorgente`,
      description: msg`Scegli da dove importare il foglio di calcolo.`,
    },
    {
      id: "sheets",
      kind: "select",
      title: msg`Fogli`,
      description: msg`Seleziona i fogli da importare (Excel multi-sheet).`,
    },
  ],

  benefits: [
    {
      icon: <FileSpreadsheet className={iconCls} aria-hidden />,
      title: msg`Excel, CSV e Google Sheets in 1 click`,
      body: msg`Carica un file locale o pesca da Drive / OneDrive senza esportare.`,
    },
    {
      icon: <Layers className={iconCls} aria-hidden />,
      title: msg`Multi-sheet selection`,
      body: msg`Scegli quali fogli del workbook importare — tabelle separate per AI.`,
    },
    {
      icon: <RefreshCcw className={iconCls} aria-hidden />,
      title: msg`Aggiornamenti via Drive sync`,
      body: msg`I file cloud restano sincronizzati: re-importi senza ricaricare.`,
    },
    {
      icon: <Sparkles className={iconCls} aria-hidden />,
      title: msg`Query AI sui dati`,
      body: msg`"Quante fatture sopra €5k nel Q1?" risponde leggendo il foglio.`,
    },
  ],

  api: inertApi,
  connectionKeyField: "id" as keyof SpreadsheetConnection,
  getDisplayName: (c) => c.connector_name ?? c.id ?? i18nSingleton._(msg`Foglio`),
  getSecondaryId: (c) => {
    const src =
      c.source_provider === "google_drive"
        ? "Google Drive"
        : c.source_provider === "onedrive"
          ? "OneDrive"
          : c.source_provider === "upload"
            ? i18nSingleton._(msg`Upload locale`)
            : null;
    if (src === null) return null;
    return c.sheet_count != null && c.sheet_count > 0
      ? `${src} · ${c.sheet_count} ${c.sheet_count === 1 ? i18nSingleton._(msg`foglio`) : i18nSingleton._(msg`fogli`)}`
      : src;
  },
  getDisplayIcon: (c) => {
    if (c.source_provider === "google_drive") return <GoogleDriveIcon className="h-5 w-5" />;
    if (c.source_provider === "onedrive") return <OneDriveIcon className="h-5 w-5" />;
    return <Cloud className="h-5 w-5" aria-hidden />;
  },
};
